import {useEffect, useState} from 'react';

import {useInputMenuContext} from 'context/InputMenu';

import addEditInputBackendHandler, {handleCommunicationSuccess, initialState} from 'helpers/addEdit/input';

import {type InputsState, SWRKeyEnum, inputMenuActionType, methodType} from 'interfaces';
import useParseURL from 'hooks/useParseURL';
import {useTranslation} from 'react-i18next';
import {
  useGetCompanies,
  useGetInput,
  useGetPlanFishTypes,
  useGetPlanTreeNavigation,
  useGetSmoltSupplies,
} from 'hooks/swr';
import {dateReader, gramsToKilos, kilosToGrams} from 'helpers/UnitsFormatHandler';
import {mutateCage, mutateSite} from 'helpers/refreshHandler';
import {useGetPlanStartEndDate} from 'hooks/useGetPlanStartEndDate';
import {type SmoltSupplyEnrichedResponse, type SimpleInputResponse} from 'client';
import {compareObjectFields} from 'utils/compareObjectFields';
import {mutateAll} from 'hooks/swr/swrHookFactory';
import {idToEntityMap} from 'utils/maps';

function useAddCageInput() {
  const {planId} = useParseURL();

  const inputMenuContext = useInputMenuContext();
  const inputMenuDispatch = inputMenuContext?.dispatch;
  const inputMenuState = inputMenuContext?.state;
  const method = inputMenuState?.method;

  const {t} = useTranslation('common');

  const formData = inputMenuState?.formData;
  const inputId = formData?.id;
  const {startDate} = useGetPlanStartEndDate();
  const {data: inputToEdit} = useGetInput({resourceId: inputId ? [planId, inputId] : undefined});
  const [inputs, setInputs] = useState<InputsState>(initialState(planId, startDate, formData));
  const {data: fishTypes} = useGetPlanFishTypes({resourceId: planId});
  const {data: companies} = useGetCompanies({resourceId: planId});
  const {data: planTreeNavigation} = useGetPlanTreeNavigation({resourceId: planId});
  const defaultCompany = companies?.find((company) => company.isDefault);
  const [isLoading, setIsLoading] = useState(false);

  const {data: smoltSupplies} = useGetSmoltSupplies({resourceId: planId});

  const smoltSupply = inputs.smoltSupplyId
    ? (smoltSupplies?.find((supply) => supply.id === inputs.smoltSupplyId) ?? null)
    : null;

  const smoltSupplyDataMismatch =
    smoltSupply &&
    Object.fromEntries(
      Object.entries(
        compareObjectFields(
          {
            certificates: inputs.certificates,
            company: inputs.companyId,
            fishGroupProperties: inputs.fishGroupPropertyIds,
            fishType: inputs.fishTypeId,
            species: inputs.species,
            weight: gramsToKilos(inputs.weight),
          },
          {
            certificates: smoltSupply.certificates,
            company: smoltSupply.company.id,
            fishGroupProperties: smoltSupply.fishGroupProperties.map((property) => property.id),
            fishType: smoltSupply.fishType.id,
            species: smoltSupply.species,
            weight: smoltSupply.weight,
          },
        ),
      ).map(([key, value]) => [key, !value]),
    );

  const handleSmoltSupplyChange = (smoltSupplyId: string | null) => {
    const newSmoltSupply = smoltSupplies?.find((supply) => supply.id === smoltSupplyId) ?? null;
    if (!newSmoltSupply) {
      setInputs({...inputs, smoltSupplyId: null});

      return;
    }

    if (inputMenuState?.method === methodType.add) {
      setInputs(mergeProperties(inputs, newSmoltSupply));
    }

    setInputs({...inputs, smoltSupplyId: newSmoltSupply.id});
  };

  const handleSyncSmoltProperties = async () => {
    if (!smoltSupply) {
      return;
    }

    setInputs(mergeProperties(inputs, smoltSupply));
  };

  const mergeProperties = (inputFields: InputsState, smoltSupply: SmoltSupplyEnrichedResponse) => {
    const {certificates, company, fishGroupProperties, fishType, id, species, weight} = smoltSupply;

    return {
      ...inputFields,
      certificates,
      companyId: company.id,
      fishTypeId: fishType.id,
      smoltSupplyId: id,
      species,
      weight: kilosToGrams(weight),
      fishGroupPropertyIds: fishGroupProperties.map((property) => property.id),
    };
  };

  useEffect(() => {
    if (defaultCompany && fishTypes && planTreeNavigation && inputMenuState?.method === methodType.add) {
      const defaultFishType = fishTypes?.[0];
      const fishTypeId = inputs.fishTypeId || defaultFishType?.id;
      const companyId = inputs.companyId || defaultCompany?.id;

      const sitesMap = idToEntityMap(planTreeNavigation.sites ?? []);

      const cageIds =
        inputs.treeSelection.cageIds.length > 0
          ? inputs.treeSelection.cageIds
          : inputs.treeSelection.siteIds.reduce<string[]>((cages, siteId) => {
              const siteCageIds = sitesMap.get(siteId)?.cages?.map((cage) => cage.id) ?? [];
              cages.push(...siteCageIds);
              return cages;
            }, []);

      setInputs({
        ...inputs,
        fishTypeId,
        companyId,
        treeSelection: {
          siteIds: inputs.treeSelection.siteIds,
          cageIds,
        },
      });
    }
  }, [defaultCompany, fishTypes, planTreeNavigation, inputMenuState?.method]);

  useEffect(() => {
    if (inputToEdit && inputMenuState?.method === methodType.edit) {
      const {date, weight, site, cage, company, fishType, fishGroupProperties, id, isLocked} = inputToEdit;
      setInputs({
        ...inputToEdit,
        date: dateReader(date),
        weight: kilosToGrams(weight),
        treeSelection: {
          siteIds: [site.id],
          cageIds: [cage.id],
        },
        planId,
        id,
        fishTypeId: fishType.id,
        companyId: company.id,
        fishGroupPropertyIds: fishGroupProperties.map((fgp) => fgp.id),
        isLocked,
      });
    }
  }, [inputToEdit, inputMenuState?.method]);

  const communicationSuccess = (newInputs: SimpleInputResponse[]) => {
    newInputs.forEach((newInput) => {
      mutateSite(newInput.siteId);
      mutateCage(newInput.cageId);
    });

    void mutateAll(SWRKeyEnum.GET_UNASSIGNED_INPUTS_TO_SMOLT_SUPPLY);
    void mutateAll(SWRKeyEnum.GET_PLAN_OR_SMOLT_SUPPLY_INPUTS);
    void mutateAll(SWRKeyEnum.GET_PLAN_SMOLT_SUPPLY);
    void mutateAll(SWRKeyEnum.GET_PLAN_ACTIONS);
    void mutateAll(SWRKeyEnum.GET_PLAN_FISH_GROUPS);
  };

  const onSubmit = async () => {
    if (!method) {
      return;
    }

    setIsLoading(true);
    const response = await addEditInputBackendHandler({
      inputs,
      method,
      createCageInput: true,
      inputId,
      communicationSuccess,
      t,
    });

    const responseData = response && 'data' in response ? response.data : null;

    handleCommunicationSuccess({
      setInputs,
      planId,
    });

    inputMenuState?.onClose && inputMenuState.onClose(responseData);
    inputMenuDispatch && inputMenuDispatch({type: inputMenuActionType.closeMenu});

    setIsLoading(false);
  };

  return {
    input: inputs,
    setInputs,
    inputMenuState,
    inputMenuDispatch,
    fishTypes,
    smoltSupplyDataMismatch,
    onSmoltSupplyChange: handleSmoltSupplyChange,
    onSyncSmoltProperties: handleSyncSmoltProperties,
    isLoading,
    onSubmit,
  };
}

export default useAddCageInput;
